import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { CopilotChangedEmploymentsResponse, CopilotChangedGroup, CopilotKeyValuesResponse, CopilotNewEmploymentsResponse, CopilotNewGroup } from '@sysarb/sysarb-types/services/v1/job-architecture';
import type { UseMutationOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { toValue, type MaybeRefOrGetter } from 'vue';
import type { ConfirmChangeInput, ObservedFields } from '@sysarb/sysarb-types/services/v1/job';
import type { Model} from '@/newApi/services/ResgateManager';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import type { StorageFilterGroup } from '@sysarb/sysarb-types/services/v1/filter-storage';
import type { BaseRegisterTableInput, TableOut } from '@sysarb/sysarb-types/services/v1/base-registry';
import type { Direction, InputFieldNames } from '@sysarb/sysarb-types/services/v1/analytics';
import { DataSources } from '@sysarb/sysarb-types/services/v1/analytics';

const jobService = new RestManager(`https://${getApiHost()}/job-service`);
const baseRegistryService = new RestManager(`https://${getApiHost()}/base-registry-service`);

export type GetEmploymentsParams = {
    filter: StorageFilterGroup;
    fields: InputFieldNames[];
    orderBy: InputFieldNames;
    orderDirection: Direction;
    page: number;
    pageSize: number;
    groupBy?: {
        field: InputFieldNames;
        // undefined = get groups, string = get rows for specific group value, null = get rows where field IS NULL
        getRowsForGroup?: string | null;
        direction: Direction;
    };
};

type JobEmploymentsOptions = {
    queries: {
        getEmployments: (params: MaybeRefOrGetter<GetEmploymentsParams>) => ReturnType<typeof queryOptions<TableOut, Error>>;
        getCopilotKeyValues: () => ReturnType<typeof queryOptions<CopilotKeyValuesResponse | null, Error>>,
        getCopilotNewGroups: () => ReturnType<typeof queryOptions<CopilotNewGroup[] | null, Error>>,
        getCopilotNewGroupEmployments: (copilotNewGroupId?: MaybeRefOrGetter<string | null>, queries?: MaybeRefOrGetter<{ page?: number, pageSize?: number }>) => ReturnType<typeof queryOptions<CopilotNewEmploymentsResponse | null, Error>>,
        getCopilotNewGroupEmploymentIds: (copilotNewGroupId: MaybeRefOrGetter<string | null>) => ReturnType<typeof queryOptions<{ ids: string[] } | null, Error>>,
        getCopilotChangedGroups: () => ReturnType<typeof queryOptions<CopilotChangedGroup[] | null, Error>>,
        getCopilotChangedGroupEmployments: (copilotChangedGroupId?: MaybeRefOrGetter<string | null>, queries?: MaybeRefOrGetter<{ page?: number, pageSize?: number }>) => ReturnType<typeof queryOptions<CopilotChangedEmploymentsResponse | null, Error>>,
        getCopilotChangedGroupEmploymentIds: (copilotChangedGroupId: MaybeRefOrGetter<string | null>) => ReturnType<typeof queryOptions<{ ids: string[] } | null, Error>>,
        getCopilotObserverFields: (subscribe?: boolean) => ReturnType<typeof queryOptions<Model<ObservedFields> | null, Error, ObservedFields>>,
    },
    mutations: {
        assignEmploymentToJobProfile: UseMutationOptions<string, Error, ConfirmChangeInput>,
    }
};

export const jobEmployments: JobEmploymentsOptions = {
    queries: {
        getEmployments: <TData = TableOut>(params: MaybeRefOrGetter<GetEmploymentsParams>): ReturnType<typeof queryOptions<TableOut, Error, TData>>  => {
            const { instance } = useLoginStore();

            return queryOptions<TableOut, Error, TData>({
                queryKey: ['employments', instance, params],
                queryFn: async (): Promise<TableOut> => {
                    const _params = toValue(params);
                    const input: BaseRegisterTableInput = {
                        filter: _params.filter,
                        fields: _params.fields,
                        datasource: DataSources.BaseRegisterEmployments,
                        orderBy: _params.orderBy,
                        orderDirection: _params.orderDirection,
                        page: _params.page,
                        pageSize: _params.pageSize,
                        groupBy: _params.groupBy,
                    };

                    try {
                        return await baseRegistryService.post<TableOut>(input, `v1/${instance}/base-register/filter`);
                    } catch (error) {
                        console.error('getEmployments', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotKeyValues: <TData = CopilotKeyValuesResponse | null>(): ReturnType<typeof queryOptions<CopilotKeyValuesResponse | null, Error, TData>> => {
            const { instance } = useLoginStore();
            const url = `v1/${instance}/copilot/key-values`;
            return queryOptions<CopilotKeyValuesResponse | null, Error, TData>({
                queryKey: [url],
                queryFn: async (): Promise<CopilotKeyValuesResponse | null> => {
                    try {
                        return await jobService.get<CopilotKeyValuesResponse>(url);
                    } catch (error) {
                        console.error('getCopilotKeyValues', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotNewGroups: <TData = CopilotNewGroup[] | null>(): ReturnType<typeof queryOptions<CopilotNewGroup[] | null, Error, TData>> => {
            const { instance } = useLoginStore();
            return queryOptions<CopilotNewGroup[] | null, Error, TData>({
                queryKey: ['copilotNewGroups', instance],
                queryFn: async (): Promise<CopilotNewGroup[] | null> => {
                    try {
                        return await jobService.get<CopilotNewGroup[]>(`v1/${instance}/copilot/new/groups`);
                    } catch (error) {
                        console.error('getCopilotNewGroups', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotNewGroupEmployments: <TData = CopilotNewEmploymentsResponse | null>(copilotNewGroupId?: MaybeRefOrGetter<string | null>, queries?: MaybeRefOrGetter<{ page?: number, pageSize?: number }>): ReturnType<typeof queryOptions<CopilotNewEmploymentsResponse | null, Error, TData>> => {
            const { instance } = useLoginStore();
            const queryString = new URLSearchParams();

            return queryOptions<CopilotNewEmploymentsResponse | null, Error, TData>({
                queryKey: [instance, copilotNewGroupId, queries],
                queryFn: async (): Promise<CopilotNewEmploymentsResponse | null> => {
                    const _queries = toValue(queries);
                    if (_queries?.page != null) queryString.set('page', _queries.page.toString());
                    if (_queries?.pageSize != null) queryString.set('pageSize', _queries.pageSize.toString());
                    const url = `v1/${instance}/copilot/new/group/${toValue(copilotNewGroupId)}/employments${queryString.toString() ? `?${queryString.toString()}` : ''}`;
                    if (copilotNewGroupId == null) return null;
                    try {
                        return await jobService.get<CopilotNewEmploymentsResponse>(url);
                    } catch (error) {
                        console.error('getCopilotNewGroupEmployments', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotChangedGroups: <TData = CopilotChangedGroup[] | null>(): ReturnType<typeof queryOptions<CopilotChangedGroup[] | null, Error, TData>> => {
            const { instance } = useLoginStore();
            return queryOptions<CopilotChangedGroup[] | null, Error, TData>({
                queryKey: ['copilotChangedGroups', instance],
                queryFn: async (): Promise<CopilotChangedGroup[] | null> => {
                    try {
                        return await jobService.get<CopilotChangedGroup[]>(`v1/${instance}/copilot/changed/groups`);
                    } catch (error) {
                        console.error('getCopilotChangedGroups', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotChangedGroupEmployments: <TData = CopilotChangedEmploymentsResponse | null>(copilotNewGroupId?: MaybeRefOrGetter<string | null>, queries?: MaybeRefOrGetter<{ page?: number, pageSize?: number }>): ReturnType<typeof queryOptions<CopilotChangedEmploymentsResponse | null, Error, TData>> => {
            const { instance } = useLoginStore();
            const queryString = new URLSearchParams();

            return queryOptions<CopilotChangedEmploymentsResponse | null, Error, TData>({
                queryKey: [instance, copilotNewGroupId, queries],
                queryFn: async (): Promise<CopilotChangedEmploymentsResponse | null> => {
                    const _queries = toValue(queries);
                    if (_queries?.page != null) queryString.set('page', _queries.page.toString());
                    if (_queries?.pageSize != null) queryString.set('pageSize', _queries.pageSize.toString());
                    const url = `v1/${instance}/copilot/changed/group/${toValue(copilotNewGroupId)}/employments${queryString.toString() ? `?${queryString.toString()}` : ''}`;
                    if (copilotNewGroupId == null) return null;
                    try {
                        return await jobService.get<CopilotChangedEmploymentsResponse>(url);
                    } catch (error) {
                        console.error('getCopilotNewGroupEmployments', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotNewGroupEmploymentIds: <TData = { ids: string[] } | null>(copilotNewGroupId: MaybeRefOrGetter<string | null>): ReturnType<typeof queryOptions<{ ids: string[] } | null, Error, TData>> => {
            const { instance } = useLoginStore();
            return queryOptions<{ ids: string[] } | null, Error, TData>({
                queryKey: ['copilotNewGroupEmploymentIds', instance, copilotNewGroupId],
                queryFn: async (): Promise<{ ids: string[] } | null> => {
                    if (copilotNewGroupId == null) return null;
                    try {
                        return await jobService.get<{ ids: string[] }>(`v1/${instance}/copilot/new/group/${toValue(copilotNewGroupId)}/employments/ids`);
                    } catch (error) {
                        console.error('getCopilotNewGroupEmploymentIds', error);
                        throw error;
                    }
                },
                enabled: toValue(copilotNewGroupId) != null,
            });
        },
        getCopilotChangedGroupEmploymentIds: <TData = { ids: string[] } | null>(copilotChangedGroupId?: MaybeRefOrGetter<string | null>): ReturnType<typeof queryOptions<{ ids: string[] } | null, Error, TData>> => {
            const { instance } = useLoginStore();
            return queryOptions<{ ids: string[] } | null, Error, TData>({
                queryKey: ['copilotChangedGroupEmploymentIds', instance, copilotChangedGroupId],
                queryFn: async (): Promise<{ ids: string[] } | null> => {
                    if (copilotChangedGroupId == null) return null;
                    try {
                        return await jobService.get<{ ids: string[] }>(`v1/${instance}/copilot/changed/group/${toValue(copilotChangedGroupId)}/employments/ids`);
                    } catch (error) {
                        console.error('getCopilotChangedGroupEmploymentIds', error);
                        throw error;
                    }
                },
                enabled: toValue(copilotChangedGroupId) != null,
            });
        },
        getCopilotObserverFields: (subscribe: boolean = true): ReturnType<typeof queryOptions<Model<ObservedFields> | null, Error, ObservedFields>> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.observedfields`;
            return queryOptions<Model<ObservedFields> | null, Error, ObservedFields>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['jobs', topic],
                queryFn: async (): Promise<Model<ObservedFields> | null> => {
                    return ResgateManager.model<ObservedFields>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
            });
        },
    },
    mutations: {
        assignEmploymentToJobProfile: {
            mutationFn: async (payload: ConfirmChangeInput): Promise<string> => {
                const { instance } = useLoginStore();
                const result = await jobService.post<string>(payload, `v1/${instance}/job-employments/confirm-job-change`);
                return result;
            }
        }
    }
};
